
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Eye, CheckCircle, Shield, Users, FileText, Search, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function DenetimGozetim() {
  const { t } = useTranslation();

  const auditServices = [
    {
      icon: Eye,
      title: t('services.denetim.preloading'),
      description: 'Ürünlerin sevkiyat öncesi kalite ve uygunluk kontrolü için gözetim hizmetleri',
      features: [
        'Ürün kalite kontrolü',
        'Ambalajlama denetimi',
        'Miktar kontrolü',
        'Belge kontrolü',
        'Yükleme gözetimi'
      ],
      image: 'https://public.readdy.ai/ai/img_res/101c8190fe5691ec1a90f1caf5942760.jpg'
    },
    {
      icon: ClipboardCheck,
      title: t('services.denetim.secondparty'),
      description: 'Tedarikçi ve iş ortaklarının kalite sistemlerinin değerlendirilmesi',
      features: [
        'Tedarikçi değerlendirmesi',
        'Kalite sistem denetimi',
        'Süreç kontrolü',
        'Uygunluk değerlendirmesi',
        'İyileştirme önerileri'
      ],
      image: 'https://readdy.ai/api/search-image?query=Second%20party%20audit%20with%20professional%20auditor%20reviewing%20supplier%20quality%20systems%2C%20manufacturing%20processes%2C%20documentation%20review%2C%20industrial%20facility%20background&width=600&height=400&seq=15&orientation=landscape'
    },
    {
      icon: Shield,
      title: t('services.denetim.hygiene'),
      description: 'Gıda ve sağlık sektörlerinde hijyen ve sanitasyon kontrolü',
      features: [
        'Hijyen standartları kontrolü',
        'Sanitasyon prosedürleri',
        'Personel hijyeni',
        'Ekipman temizliği',
        'HACCP uygulamaları'
      ],
      image: 'https://readdy.ai/api/search-image?query=Hygiene%20and%20sanitation%20inspection%20with%20inspector%20checking%20food%20safety%20protocols%2C%20clean%20kitchen%20environment%2C%20sanitization%20procedures%2C%20health%20standards%20compliance&width=600&height=400&seq=16&orientation=landscape'
    },
    {
      icon: Search,
      title: 'Memnuniyet Anketi Denetimleri',
      description: 'Müşteri memnuniyeti ve hizmet kalitesi değerlendirme denetimleri',
      features: [
        'Müşteri memnuniyet ölçümü',
        'Hizmet kalitesi değerlendirmesi',
        'Geri bildirim analizi',
        'İyileştirme alanları belirleme',
        'Performans raporlama'
      ],
      image: 'https://readdy.ai/api/search-image?query=Customer%20satisfaction%20survey%20audit%20with%20professional%20conducting%20service%20quality%20assessment%2C%20customer%20feedback%20forms%2C%20business%20environment%2C%20quality%20measurement%20tools&width=600&height=400&seq=17&orientation=landscape'
    }
  ];

  const auditProcess = [
    {
      step: '01',
      title: 'Denetim Planlaması',
      description: 'Denetim kapsamının belirlenmesi ve program hazırlama'
    },
    {
      step: '02',
      title: 'Ön Hazırlık',
      description: 'Dokümantasyon incelemesi ve denetim ekibi oluşturma'
    },
    {
      step: '03',
      title: 'Sahada Denetim',
      description: 'Yerinde inceleme, gözlem ve değerlendirme'
    },
    {
      step: '04',
      title: 'Bulguların Analizi',
      description: 'Denetim bulgularının değerlendirilmesi ve analizi'
    },
    {
      step: '05',
      title: 'Rapor Hazırlama',
      description: 'Detaylı denetim raporunun düzenlenmesi'
    },
    {
      step: '06',
      title: 'Takip ve İzleme',
      description: 'Düzeltici faaliyetlerin takibi ve izlenmesi'
    }
  ];

  const auditTypes = [
    {
      title: 'Kalite Denetimleri',
      description: 'Ürün ve hizmet kalitesinin değerlendirilmesi',
      icon: CheckCircle
    },
    {
      title: 'Süreç Denetimleri',
      description: 'İş süreçlerinin etkinlik ve verimlilik kontrolü',
      icon: FileText
    },
    {
      title: 'Uygunluk Denetimleri',
      description: 'Standart ve yasal gerekliliklere uygunluk kontrolü',
      icon: Shield
    },
    {
      title: 'Performans Denetimleri',
      description: 'Organizasyonel performans ve hedef değerlendirmesi',
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section 
          className="relative py-24 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://readdy.ai/api/search-image?query=Professional%20audit%20and%20surveillance%20services%20with%20inspector%20conducting%20quality%20assessment%2C%20documentation%20review%2C%20industrial%20facility%2C%20compliance%20checking%20procedures&width=1920&height=800&seq=18&orientation=landscape')`
          }}
        >
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl font-bold mb-6">{t('services.denetim.title')}</h1>
              <p className="text-xl leading-relaxed">
                {t('services.denetim.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Main Services */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Denetim ve Gözetim Hizmetlerimiz</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Kalite güvencesi ve süreç iyileştirme için kapsamlı denetim hizmetleri
              </p>
            </div>

            <div className="space-y-16">
              {auditServices.map((service, index) => (
                <div key={index} className={`flex flex-col lg:flex-row items-center gap-12 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className="lg:w-1/2">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                  <div className="lg:w-1/2">
                    <div className="flex items-center mb-4">
                      <service.icon className="w-8 h-8 text-red-600 mr-3" />
                      <h3 className="text-3xl font-bold text-gray-800">{service.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button className="bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer">
                      Denetim Talep Et
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Audit Types */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Denetim Türleri</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Farklı ihtiyaçlara yönelik özelleştirilmiş denetim hizmetleri
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {auditTypes.map((type, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <type.icon className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{type.title}</h3>
                  <p className="text-gray-600">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Audit Process */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Denetim Süreci</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Sistematik ve objektif denetim yaklaşımımızla güvenilir sonuçlar
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {auditProcess.map((step, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Audit Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Neden Denetim ve Gözetim?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Profesyonel denetim hizmetleri ile kalite ve güvenilirliği artırın
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Risk Azaltma</h3>
                <p className="text-gray-600">Potansiyel riskleri erken tespit ve önleme</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Kalite Güvencesi</h3>
                <p className="text-gray-600">Sürekli kalite kontrolü ve iyileştirme</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Objektif Değerlendirme</h3>
                <p className="text-gray-600">Bağımsız ve tarafsız denetim yaklaşımı</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Müşteri Güveni</h3>
                <p className="text-gray-600">Artan güvenilirlik ve müşteri memnuniyeti</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-red-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold mb-6">Denetim Hizmetlerimizden Yararlanın</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Uzman denetim ekibimiz ile kalite standartlarınızı yükseltin ve 
              süreçlerinizi sürekli iyileştirin. Güvenilir denetim hizmetleri için bizimle iletişime geçin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-red-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                Denetim Talebi
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer">
                Bilgi Al
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
